import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Button as Button95, Checkbox, Divider, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { Link, RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { updateVisuals } from '@/state/uploads';
import { Community } from "@/types/community";
import { UserInfo } from "@/types/user_info";
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { PasswordShowHideButton } from '@/components/PasswordShowHideButton';
import { PasswordRequirementsButton } from '@/components/PasswordRequirementsButton';
import { CommunityMembership } from '@/types/community_membership';
import { HumanVerifier } from '@/components/HumanVerifier';
import { HUMAN_VERIFIER_NON_WEB_SUCCESS, IHumanVerifier } from '@/types/human_verifier_props';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
});

export default function Signup() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const emailInput = useRef<RNTextInput>(null);
  const passwordInput = useRef<RNTextInput>(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeAge, setAgreeAge] = useState(false);
  const [newPasswordShown, setNewPasswordShown] = useState(false);
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const domain = useSelector((state: RootState) => state.user.domain);
  const [humanVerifierToken, setHumanVerifierToken] = useState<string>("");
  const humanVerifier = useRef<IHumanVerifier>(null);

  const { register, control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    }
  });

  const [email, password] = watch(["email", "password"]);

  const onSubmit = useCallback(async (data: any) => {
    if (!agreeTerms) {
      AppUtils.showErrorToast("You must agree to the terms and conditions", null, false);
      return;
    } else if (!agreeAge) {
      AppUtils.showErrorToast("You must be 18 years or older to join", null, false);
      return;
    }
    try {
      showSpinner();

      if (AppUtilities.isWeb()) {
        if (humanVerifierToken.length == 0) {
          throw new Error("Please complete the challenge");
        }
        if (humanVerifierToken != HUMAN_VERIFIER_NON_WEB_SUCCESS) {
          AppUtilities.info("Signup.onSubmit checking human verification");
          
          // This will throw an error if there's a failure
          await AppUtilities.fetchUnauthorized(
            "Signup.onSubmit",
            "/rabbl/api/turnstile",
            "POST",
            {
              token: humanVerifierToken,
            }
          );
        }
      }

      const response = await AppUtilities.fetchUnauthorized(
        "Signup.onSubmit",
        "/rabbl/api/signup",
        "POST",
        {
          email: data.email,
          password: data.password,
          domain: isMainLogicalHost ? null : domain,
        }
      );

      // This does the login in redux based on the loggedIn response body attribute
      dispatch(setData(response));
      // Reset visuals if we previously created a Rabbl
      dispatch(updateVisuals([]));

      if (isMainLogicalHost) {
        AppUtils.Globals.nextRootRedirect = "/rabbls" as RelativePathString;
      } else {
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        router.replace(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
      }

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Signup.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain, agreeTerms, agreeAge, isMainLogicalHost, humanVerifier, humanVerifierToken]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  let createDisabled = false;
  if (!email || email.length == 0) {
    createDisabled = true;
  }
  if (!password || password.length == 0) {
    createDisabled = true;
  }
  if (!agreeTerms) {
    createDisabled = true;
  }
  if (!agreeAge) {
    createDisabled = true;
  }
  if (AppUtilities.isWeb() && humanVerifierToken.length == 0) {
    createDisabled = true;
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={"Create account"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Create a new account</AppText>

          <Controller
            control={control}
            name="email"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={emailInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Email"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="email"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  autoCapitalize='none'
                  onSubmitEditing={() => passwordInput.current?.focus()}
                />
                {AppUtils.PASSWORD_REQUIREMENTS_BLANK}
              </View>
            )}
          />
          {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Email is required. ^</AppText>}

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={passwordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Password"
                  style={styles.formElement}
                  returnKeyType="go"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                  secureTextEntry={newPasswordShown ? false : true}
                />
                <PasswordRequirementsButton />
                <PasswordShowHideButton passwordShown={newPasswordShown} setPasswordShown={setNewPasswordShown} />
              </View>
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Password is required. ^</AppText>}

          <AppText>Review our <Link href="/policies" style={CustomTextStyles.link}>terms and conditions</Link></AppText>
          
          <Checkbox
            status={agreeTerms ? 'checked' : 'unchecked'}
            onPress={() => setAgreeTerms(agreeTerms ? false : true)}
            label="I agree to the terms and conditions"
          />

          <Checkbox
            status={agreeAge ? 'checked' : 'unchecked'}
            onPress={() => setAgreeAge(agreeAge ? false : true)}
            label="I am at least 18 years old"
          />

          { AppUtilities.isWeb() && (
              <View style={AppConstants.STYLES.alignCenter}>
                <HumanVerifier onSuccess={setHumanVerifierToken} ref={humanVerifier} />
              </View>
          )}

          <View style={styles.inputWrapper}>
            <Button95
              onPress={handleSubmit(onSubmit, onValidationErrors)}
              disabled={createDisabled}
            >
              <AppText
                style={AppConstants.STYLES.fontBold}
                disabled={createDisabled}
              >Create account</AppText>
            </Button95>
          </View>

          <Divider />

          <AppText>Already have an account?</AppText>
          <Link href="/signin" asChild className={"nontext"}><Button95>{AppConsts.EXISING_USERS_LOGIN}</Button95></Link>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
